// The gun kill look: the extra effect when a gun hit sets off an armed sea
// mine (v2-the-deep.md §11.2, §11.8, R3031, R3032, ui-map-interface.md
// §6.1.14; mockup round 138, `archive/mockups/138-gun-kill-mine-effects.html`).
//
// A gun shot at a sea object draws the salvo and the hit of the combat
// layer, as a shot at a hull does. When the first round of the salvo
// arrives at an armed sea mine, the sea mine draws its own detonation
// look (round 070) at once, and this look plays over it on the struck
// sea mine alone, never on a link of a Petard chain. The owner picked
// card 02 Casing Shards with the strike flash of card 01 (R3032), with no
// delay of the look of the sea mine.
//
// `GUN_KILL_LOOKS` is the seam of the effect: a later look adds its id to
// `GUN_KILL_STYLES` and its entry beside this one, and `GUN_KILL_STYLE`
// (or a World theme field that takes its place) picks one.
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.

import { SEA_INK, fade, line, lw, outCubic, smooth, soft, type Ctx } from './seaobject-kit';

/** The ids of the gun kill looks. */
export const GUN_KILL_STYLES = ['casing-shards'] as const;

/** One gun kill look. */
export type GunKillStyle = (typeof GUN_KILL_STYLES)[number];

/** One gun kill, as a look reads it. The layer keeps one object for every
 *  frame, so a frame builds none. */
export interface GunKill {
  /** The struck sea mine, map px. */
  x: number;
  y: number;
  /** The unit bearing of the shot, from the shooter to the sea mine. */
  ux: number;
  uy: number;
  /** The outer edge of the detonation look of the kind, map px. */
  R: number;
  /** The seeded spread of the kill (`GunKillLook.spread`), written once. */
  spread: Float64Array;
  /** The map zoom: a hairline keeps 0.9 screen px at least. */
  z: number;
}

/** One look: its life in wall seconds, the length of its spread, the
 *  spread it seeds once per kill, and its paint at `k` seconds since the
 *  first round arrived. */
export interface GunKillLook {
  life: number;
  spreadLen: number;
  spread(rnd: () => number, out: Float64Array): void;
  paint(c: Ctx, o: GunKill, k: number): void;
}

const WHITE = SEA_INK.white, PALE = SEA_INK.pale;

// ---- card 01, the strike flash ---------------------------------------------------

/** The flash lasts this long, s. */
const FLASH_SEC = 0.1;
/** The flash stands this far back along the shot from the centre, map px:
 *  the near side of the casing, where the round strikes. */
const FLASH_BACK_PX = 3.5;
/** The soft disc of the flash: its radius, map px, and its alpha. */
const FLASH_GLOW_PX = 5, FLASH_GLOW_ALPHA = 0.9;
/** The four rays: the length at the start and the growth, map px, and the
 *  stroke. The rays stand at 45° to the shot and 90° apart. */
const RAY_PX = 3, RAY_GROW_PX = 5, RAY_WIDTH = 1.3;

function strikeFlash(c: Ctx, o: GunKill, k: number): void {
  if (k < 0 || k > FLASH_SEC) return;
  const u = k / FLASH_SEC, a = 1 - u;
  const x = o.x - o.ux * FLASH_BACK_PX, y = o.y - o.uy * FLASH_BACK_PX;
  const r = RAY_PX + RAY_GROW_PX * outCubic(u * 2), ang = Math.atan2(o.uy, o.ux);
  soft(c, x, y, FLASH_GLOW_PX, WHITE, FLASH_GLOW_ALPHA * a);
  for (let i = 0; i < 4; i++) {
    const th = ang + Math.PI / 4 + i * Math.PI / 2;
    line(c, x, y, x + Math.cos(th) * r, y + Math.sin(th) * r, WHITE, a, lw(RAY_WIDTH, o.z));
  }
}

// ---- card 02, the casing shards --------------------------------------------------

/** The shards of the casing. */
export const SHARDS = 6;
/** The half width of the cone of the shards round the bearing, rad. */
export const SHARD_CONE = 0.7;
/** The shards end this far past the edge of the look, plus up to the
 *  spread, map px. */
const SHARD_PAST_PX = 3, SHARD_SPREAD_PX = 8;
/** A shard starts this far out, map px, and flies for this long, s. */
const SHARD_START_PX = 2, SHARD_FLY_SEC = 0.6;
/** The sink of a shard, map px, from and to, s. */
const SHARD_SINK_PX = 2.5, SHARD_SINK_FROM = 0.3, SHARD_SINK_TO = 0.75;
/** The fade of a shard, from and to, s; the look ends with it. */
const SHARD_FADE_FROM = 0.4, SHARD_FADE_TO = 0.75;
/** The stroke of a shard: its length at the start and its shrink over the
 *  flight, map px, its alpha and its width. */
const SHARD_LEN_PX = 3.2, SHARD_SHRINK_PX = 1.6, SHARD_ALPHA = 0.95, SHARD_WIDTH = 1.5;
/** A shard is white for this long, then the pale ink of metal, s. */
const SHARD_HOT_SEC = 0.1;

function casingShards(c: Ctx, o: GunKill, k: number): void {
  if (k < 0 || k > SHARD_FADE_TO) return;
  const ang = Math.atan2(o.uy, o.ux), s = o.spread;
  const sink = SHARD_SINK_PX * smooth(SHARD_SINK_FROM, SHARD_SINK_TO, k);
  const a = SHARD_ALPHA * fade(k, SHARD_FADE_FROM, SHARD_FADE_TO);
  const L = SHARD_LEN_PX - SHARD_SHRINK_PX * smooth(0, SHARD_FLY_SEC, k);
  const ink = k < SHARD_HOT_SEC ? WHITE : PALE, w = lw(SHARD_WIDTH, o.z);
  for (let i = 0; i < SHARDS; i++) {
    const th = ang + s[i], reach = o.R + SHARD_PAST_PX + s[SHARDS + i];
    const d = SHARD_START_PX + reach * outCubic(k / SHARD_FLY_SEC), cs = Math.cos(th), sn = Math.sin(th);
    const x = o.x + cs * d, y = o.y + sn * d + sink;
    line(c, x - cs * L, y - sn * L, x, y, ink, a, w);
  }
}

/** Card 02 with the flash of card 01: the cone offset of each shard, then
 *  the reach past the edge of each shard, drawn in the order of the card. */
const casingShardsLook: GunKillLook = {
  life: SHARD_FADE_TO,
  spreadLen: 2 * SHARDS,
  spread(rnd, out) {
    for (let i = 0; i < SHARDS; i++) {
      out[i] = (rnd() * 2 - 1) * SHARD_CONE;
      out[SHARDS + i] = rnd() * SHARD_SPREAD_PX;
    }
  },
  paint(c, o, k) {
    casingShards(c, o, k);
    strikeFlash(c, o, k);
  },
};

/** The look of each id. The record is typed by the list, so a look the
 *  list gains without its entry is a type error. */
export const GUN_KILL_LOOKS: Record<GunKillStyle, GunKillLook> = {
  'casing-shards': casingShardsLook,
};

/** The look the map plays (R3032). */
export const GUN_KILL_STYLE: GunKillStyle = 'casing-shards';
